import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import C2C from "react-copy-to-clipboard";
import { Link as RouterLink } from "react-router-dom";

import { useTheme } from "@material-ui/core/styles";
import { Grid, IconButton, Typography, makeStyles, Tooltip } from "@material-ui/core";
import Link from "@material-ui/core/Link";
import { FileCopyOutlined } from "@material-ui/icons";
import InfoIcon from "@material-ui/icons/Info";

const useStyles = (textColor, labelColor, strikeThrough) =>
  makeStyles((theme) => ({
    row: {
      display: "flex",
      alignItems: "center",
    },
    shorterRow: {
      margin: "-8px 0",
    },
    label: {
      paddingRight: "1rem",
      display: "flex",
      alignItems: "center",
      textDecorationColor:
        labelColor === "error" && strikeThrough ? theme.palette.error.main : "none",
      textDecoration: labelColor === "error" && strikeThrough ? "line-through" : "none",
    },
    noWrap: {
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    truncate: {
      display: "flex",
      alignItems: "center",
      flex: "0 1 auto",
      "&, & > * ": {
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    trailing: {
      flex: 1,
      marginLeft: "1em",
    },
    copyable: {
      cursor: "pointer",
    },
    valueColumn: {
      color:
        textColor === "textSecondary" && strikeThrough
          ? theme.palette.text.secondary
          : theme.palette.text.primary,
      textDecoration: labelColor === "error" && strikeThrough ? "line-through" : "none",
    },
  }));

const KeyValueDisplay = ({
  label,
  text,
  defaultText,
  copyToClipboard,
  noWrap,
  keyTypographyProps,
  valueTypographyProps,
  labelColor,
  textColor,
  minWidth,
  maxTextWidth,
  textLinkTo,
  textLinkExternal,
  justify,
  separator,
  labelLinkTo,
  keyTooltip,
  tooltip,
  tooltipIconStyle,
  tooltipPlacement,
  labelMinWidth,
  className,
  strikeThrough,
  testIds,
}) => {
  const theme = useTheme();
  const classes = useStyles(textColor, labelColor, strikeThrough)(theme);

  return (
    <Grid
      item
      xs={12}
      className={clsx(
        classes.row,
        noWrap && classes.noWrap,
        copyToClipboard && classes.shorterRow,
        className,
      )}
      style={{ justifyContent: justify, ...(minWidth ? { minWidth } : {}) }}
    >
      {label && (
        <div
          className={classes.label}
          style={{ ...(labelMinWidth ? { minWidth: labelMinWidth } : {}) }}
        >
          {labelLinkTo ? (
            <Link
              color={labelColor}
              component={RouterLink}
              to={labelLinkTo}
              data-testid={testIds.label}
              {...keyTypographyProps}
            >
              {label}
              {separator}
            </Link>
          ) : (
            <Typography color={labelColor} data-testid={testIds.label} {...keyTypographyProps}>
              {label}
              {separator}
            </Typography>
          )}
          {keyTooltip && tooltip && (
            <Tooltip title={tooltip} placement={tooltipPlacement}>
              <InfoIcon
                style={{ fontSize: "1rem", marginLeft: "0.5rem", ...tooltipIconStyle }}
                data-testid={testIds.tooltipIcon}
              />
            </Tooltip>
          )}
        </div>
      )}
      <div
        className={clsx(noWrap && classes.truncate, copyToClipboard && classes.copyable)}
        style={maxTextWidth && { maxWidth: maxTextWidth }}
      >
        {!keyTooltip && tooltip && (
          <Tooltip title={tooltip} placement="left">
            <InfoIcon
              style={{ fontSize: "1rem", marginRight: "0.5rem", ...tooltipIconStyle }}
              data-testid={testIds.tooltipIcon}
            />
          </Tooltip>
        )}
        <C2C text={copyToClipboard && text}>
          {textLinkTo ? (
            <Link
              className={classes.valueColumn}
              component={textLinkExternal ? "a" : RouterLink}
              to={textLinkTo}
              href={textLinkTo}
              rel={textLinkExternal && "noopener noreferrer"}
              target={textLinkExternal && "_blank"}
              data-testid={testIds.text}
            >
              {text}
            </Link>
          ) : (
            <Typography
              className={classes.valueColumn}
              data-testid={testIds.text}
              {...valueTypographyProps}
            >
              {text || defaultText}
            </Typography>
          )}
        </C2C>
      </div>
      {copyToClipboard && (
        <div className={clsx(classes.trailing)}>
          <C2C text={copyToClipboard && text}>
            <IconButton aria-label="Copy" data-testid={testIds.clipboard}>
              <FileCopyOutlined />
            </IconButton>
          </C2C>
        </div>
      )}
    </Grid>
  );
};

KeyValueDisplay.propTypes = {
  label: PropTypes.string,
  text: PropTypes.string,
  defaultText: PropTypes.string,
  copyToClipboard: PropTypes.bool,
  noWrap: PropTypes.bool,
  keyTypographyProps: PropTypes.object,
  valueTypographyProps: PropTypes.object,
  labelColor: PropTypes.oneOf([
    "initial",
    "inherit",
    "primary",
    "secondary",
    "textPrimary",
    "textSecondary",
    "error",
  ]),
  textColor: PropTypes.PropTypes.oneOf([
    "initial",
    "inherit",
    "primary",
    "secondary",
    "textPrimary",
    "textSecondary",
    "error",
  ]),
  maxTextWidth: PropTypes.string,
  textLinkTo: PropTypes.string,
  textLinkExternal: PropTypes.bool,
  justify: PropTypes.string,
  minWidth: PropTypes.string,
  separator: PropTypes.string,
  labelLinkTo: PropTypes.string,
  tooltip: PropTypes.string,
  tooltipIconStyle: PropTypes.string,
  tooltipPlacement: PropTypes.oneOf([
    "top",
    "bottom",
    "left",
    "right",
    "top-start",
    "top-end",
    "bottom-start",
    "bottom-end",
    "left-start",
    "left-end",
    "right-start",
    "right-end",
  ]),
  labelMinWidth: PropTypes.string,
  className: PropTypes.string,
  strikeThrough: PropTypes.bool,
  keyTooltip: PropTypes.bool,
  testIds: PropTypes.object,
};

KeyValueDisplay.defaultProps = {
  label: "",
  text: "",
  defaultText: "",
  copyToClipboard: false,
  noWrap: true,
  keyTypographyProps: {},
  valueTypographyProps: {},
  labelColor: "initial",
  textColor: "initial",
  maxTextWidth: null,
  textLinkTo: null,
  textLinkExternal: false,
  justify: "start",
  minWidth: null,
  separator: ":",
  labelLinkTo: null,
  tooltip: null,
  tooltipIconStyle: null,
  tooltipPlacement: "right",
  labelMinWidth: null,
  className: "",
  strikeThrough: false,
  keyTooltip: true,
  testIds: {},
};

export default KeyValueDisplay;
